import type { Word } from '../types';

let webpSupported: boolean | null = null;

function detectWebP(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    if (!canvas.getContext || !canvas.getContext('2d')) return false;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
}

function isWebPSupported(): boolean {
  if (webpSupported === null) webpSupported = detectWebP();
  return webpSupported;
}

/**
 * Resolve an image src to a public path, swapping in .webp if supported
 * and the source is in our /images/ folder (R2 /uploads/ already serves webp directly).
 */
export function imgSrc(src: string | undefined | null): string {
  if (!src) return '';
  let out = src;
  if (out.startsWith('images/')) out = '/' + out;
  if (out.startsWith('/images/') && isWebPSupported()) {
    out = out.replace(/\.(png|jpe?g)$/i, '.webp');
  }
  return out;
}

/** Get the first image for a word, falling back to legacy single-image field. */
export function firstImage(item: Word): string {
  if (Array.isArray(item.images) && item.images.length > 0) return imgSrc(item.images[0]);
  if (item.image) return imgSrc(item.image);
  return '';
}

/** Get all images for a word (images[] or single legacy field). */
export function allImages(item: Word): string[] {
  if (Array.isArray(item.images) && item.images.length > 0) return item.images.map(imgSrc);
  if (item.image) return [imgSrc(item.image)];
  return [];
}

/** Get first available sound for an item. */
export function firstSound(item: Word): string {
  if (Array.isArray(item.sounds) && item.sounds.length > 0) return item.sounds[0];
  return item.sound || '';
}

/** Transcription sound (name said out loud). Empty string if not set. */
export function transcriptionSrc(item: Word): string {
  return item.sound || '';
}

/** English transcription sound (name said out loud in English). Empty string if not set. */
export function transcriptionSrcEn(item: Word): string {
  return item.sound_en || '';
}

/** Cómo se lee una palabra en voz alta: el texto, la grabación si existe y el
 *  idioma. En inglés cae al español cuando la palabra no tiene `word_en`, y
 *  entonces también la grabación y el idioma son los del español: sin esto un
 *  juego leía "perro" con la voz en-US. */
export function wordReading(item: Word, english: boolean): { text: string; src: string; lang?: string } {
  if (english && item.word_en) {
    return { text: item.word_en, src: transcriptionSrcEn(item), lang: 'en-US' };
  }
  return { text: item.word, src: transcriptionSrc(item) };
}

/** Effect sounds (how the thing sounds — dog bark, music clip, etc). */
export function effectSounds(item: Word): string[] {
  if (!Array.isArray(item.sounds)) return [];
  return item.sounds.filter((s): s is string => typeof s === 'string' && s.length > 0);
}

/** Pick a random effect sound from the array, or empty if none. */
export function pickEffectSound(item: Word): string {
  const arr = effectSounds(item);
  if (arr.length === 0) return '';
  return arr[Math.floor(Math.random() * arr.length)];
}
